import axios from "axios";
import { USER_API, getApiHeader } from "./apiConfig";

export const MASTER_SEARCH_PATHS = {
  accountGroup: "masters/account-group/search",
  asset: "masters/asset/search",
  certificateId: "masters/certificate-id/search",
  company: "masters/company/search",
  companyCode: "masters/company-code/search",
  companyCountry: "masters/company/search",
  costCenter: "masters/cost-center/search",
  country: "masters/country/search",
  currency: "masters/currency/search",
  departments: "masters/departments/search",
  exchangeRate: "masters/exchange-rate/search",
  functionalArea: "masters/functional-area/search",
  glAccount: "masters/gl-account/search",
  incoterms: "masters/incoterms/search",
  materialGroup: "masters/material-group/search",
  paymentTerms: "masters/payment-terms/search",
  plant: "masters/plant/search",
  plantPurchaseGroup: "masters/plant-purchase-group/search",
  poPricingConditions: "masters/po-pricing-conditions/search",
  pricingCondition: "masters/pricing-condition/search",
  purchaseGroup: "masters/purchasing-group/search",
  purchasingOrganization: "masters/purchasing-organization/search",
  region: "masters/region/search",
  storagePlant: "masters/storage-plant/search",
  subDepartments: "masters/sub-departments/search",
  taxCategory: "masters/tax-category/search",
  taxCode: "masters/tax-code/search",
  uom: "masters/uom/search",
  vendorCategory: "masters/vendor-category/search",
  wtExepRes: "masters/wt-exep-res/search",
  wtRecType: "masters/wt-rec-type/search",
  wtTaxCode: "masters/wt-tax-code/search",
};

/** Filter field used when looking up a master row by code. */
export const CODE_FILTER_FIELDS = {
  accountGroup: "accountGroup",
  asset: "assetNumber",
  certificateId: "certificateId",
  company: "companyCode",
  companyCode: "companyCode",
  companyCountry: "companyCode",
  costCenter: "costCenter",
  country: "countryKey",
  currency: "currencyKey",
  departments: "departmentName",
  exchangeRate: "fromCurrency",
  functionalArea: "functionalArea",
  glAccount: "glAccount",
  incoterms: "incoterms",
  materialGroup: "mgCode",
  paymentTerms: "code",
  plant: "plantnumber",
  plantPurchaseGroup: "plant",
  poPricingConditions: "pricingConditions",
  pricingCondition: "type",
  purchaseGroup: "purchaseGroup",
  purchasingOrganization: "purchaseOrganization",
  region: "region",
  storagePlant: "storageLocation",
  subDepartments: "subDepartmentName",
  taxCategory: "taxType",
  taxCode: "taxCode",
  uom: "uom",
  vendorCategory: "code",
  wtExepRes: "wtaxReason",
  wtRecType: "wtaxReci",
  wtTaxCode: "wtaxCode",
};

export const unwrapMasterRows = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.content)) return response.content;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.result)) return response.result;
  if (Array.isArray(response.records)) return response.records;
  if (response.data && typeof response.data === "object") {
    if (Array.isArray(response.data.content)) return response.data.content;
    if (Array.isArray(response.data.data)) return response.data.data;
    if (Array.isArray(response.data.result)) return response.data.result;
    if (Array.isArray(response.data.records)) return response.data.records;
  }
  return [];
};

export const unwrapMasterPage = (payload, rows = []) => {
  if (!payload || typeof payload !== "object") {
    return {
      page: 0,
      size: rows.length || 10,
      totalElements: rows.length,
      totalPages: 1,
    };
  }
  const nested =
    payload.page && typeof payload.page === "object" ? payload.page : null;
  const pageNum =
    nested?.page ?? (typeof payload.page === "number" ? payload.page : 0);
  const size = nested?.size ?? payload.size ?? rows.length ?? 10;
  const totalElements =
    nested?.totalElements ?? payload.totalElements ?? rows.length;
  const totalPages =
    nested?.totalPages ??
    payload.totalPages ??
    Math.max(1, Math.ceil((totalElements || 0) / (size || 1)));
  return {
    page: pageNum,
    size,
    totalElements,
    totalPages,
    first: nested?.first ?? payload.first,
    last: nested?.last ?? payload.last,
  };
};

const buildCodeFilters = (field, codes = []) => {
  const values = (Array.isArray(codes) ? codes : [codes])
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);
  if (!values.length) return [];
  return [
    {
      field,
      operator: values.length === 1 ? "EQUAL" : "IN",
      values,
    },
  ];
};

const postMasterSearch = async (
  path,
  { page = 0, size = 200, sorts = [], filters = [] } = {}
) => {
  const body = {
    page: { page, size },
    filters: Array.isArray(filters) ? filters : [],
  };
  if (Array.isArray(sorts) && sorts.length) {
    body.sorts = sorts;
  }
  const response = await axios.post(
    `${USER_API}${path}`,
    body,
    getApiHeader()
  );
  return response.data;
};

export const searchMaster = async (type, options = {}) => {
  const path = MASTER_SEARCH_PATHS[type];
  if (!path) {
    throw new Error(`Unknown master search type: ${type}`);
  }
  return postMasterSearch(path, options);
};

export const searchMasterPaged = async (type, options = {}) => {
  const payload = await searchMaster(type, options);
  const rows = unwrapMasterRows(payload);
  return {
    data: rows,
    page: unwrapMasterPage(payload, rows),
    raw: payload,
  };
};

export const fetchMasterList = async (
  type,
  {
    page = 0,
    size = 10,
    search = "",
    searchField,
    extraFilters = [],
    sorts,
  } = {}
) => {
  const filters = [...(Array.isArray(extraFilters) ? extraFilters : [])];
  const keyword = String(search || "").trim();
  const field = searchField || CODE_FILTER_FIELDS[type];
  if (keyword && field) {
    filters.push({ field, operator: "LIKE", values: [keyword] });
  }
  return searchMasterPaged(type, { page, size, sorts, filters });
};

/** Lookup master rows by code(s) for a given master type. */
export const searchMasterByCodes = async (
  type,
  codes = [],
  { size = 200, extraFilters = [] } = {}
) => {
  const field = CODE_FILTER_FIELDS[type];
  if (!field) return [];
  const filters = [
    ...buildCodeFilters(field, codes),
    ...(Array.isArray(extraFilters) ? extraFilters : []),
  ];
  if (!filters.length) return [];
  const data = await searchMaster(type, { page: 0, size, filters });
  return unwrapMasterRows(data);
};

export const getMasterById = async (type, id) => {
  const searchPath = MASTER_SEARCH_PATHS[type];
  if (!searchPath || id == null || id === "") return null;
  const resourcePath = searchPath.replace(/\/search$/, "");
  const response = await axios.get(
    `${USER_API}${resourcePath}/${encodeURIComponent(id)}`,
    getApiHeader()
  );
  return response.data;
};

export const MasterSearchService = {
  searchMaster,
  searchMasterPaged,
  fetchMasterList,
  searchMasterByCodes,
  getMasterById,
  MASTER_SEARCH_PATHS,
  CODE_FILTER_FIELDS,
  unwrapMasterRows,
  unwrapMasterPage,
};

export default MasterSearchService;
